import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';

const STRIPE_PRICE_ASSISTENT = 'price_1TMUJwDx4D9iXVqTuMyeDjLM';
const STRIPE_PRICE_BUREAU    = 'price_1TMULdDx4D9iXVqTJmeBNRb3';

const PLANS = [
  {
    id: 'assistent',
    name: 'Assistent',
    price: '€29',
    period: '/maand',
    priceId: STRIPE_PRICE_ASSISTENT,
    features: [
      'AI Chat (onbeperkt)',
      'Afspraken / basisagenda (onbeperkt)',
      'FAQ Inbox (15 antwoorden/maand)',
      'Reisvoorstellen (5/maand)',
    ],
    highlighted: false,
  },
  {
    id: 'bureau',
    name: 'Bureau',
    price: '€89',
    period: '/maand',
    priceId: STRIPE_PRICE_BUREAU,
    features: [
      'Alles uit Assistent, onbeperkt',
      'Boekingen + PDF-extractie + verrijking',
      'Reisbescheiden',
      'Klanten + klantinzichten',
      'Nieuwsbrieven & marketing',
      'Auto-emails (pre- en post-trip)',
      'Volledige AI-instructies + trainingsdocumenten',
      'Afspraken met AI e-mailgeneratie',
    ],
    highlighted: true,
  },
];

const TIER_LABEL = { none: 'Gratis', assistent: 'Assistent', bureau: 'Bureau' };

export default function Pricing() {
  const { authFetch } = useAuth();
  const { tier, refreshSubscription } = useSubscription();
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);

  const handleUpgrade = async (plan) => {
    if (!plan.priceId) {
      setError('Prijsconfiguratie ontbreekt. Neem contact op met support.');
      return;
    }
    setLoading(plan.id);
    setError(null);
    try {
      const res = await authFetch('/api/account/checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priceId: plan.priceId }),
      });
      const data = await res.json();
      if (data?.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        setError(data?.error || 'Checkout aanmaken mislukt');
      }
    } catch (err) {
      setError(err.message || 'Checkout aanmaken mislukt');
    } finally {
      setLoading(null);
    }
  };

  const handleManageBilling = async () => {
    setLoading('portal');
    setError(null);
    try {
      const res = await authFetch('/api/account/billing');
      const data = await res.json();
      if (data?.customerPortalUrl) {
        window.location.href = data.customerPortalUrl;
      } else {
        setError('Facturatiepagina niet beschikbaar');
      }
    } catch (err) {
      setError(err.message || 'Facturatiepagina ophalen mislukt');
    } finally {
      setLoading(null);
    }
  };

  const isCurrentPlan = (planId) => tier === planId;
  const isPaidTier = tier !== 'none';

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      {/* Header */}
      <div className="mb-10">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Abonnementen</h1>
        <p className="text-gray-500">
          Huidig pakket: <span className="font-medium text-gray-700">{TIER_LABEL[tier] || 'Gratis'}</span>
          {isPaidTier && (
            <button
              onClick={handleManageBilling}
              disabled={loading === 'portal'}
              className="ml-4 text-blue-600 hover:underline text-sm disabled:opacity-50"
            >
              {loading === 'portal' ? 'Laden...' : 'Abonnement beheren →'}
            </button>
          )}
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Plan cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        {PLANS.map((plan) => {
          const current = isCurrentPlan(plan.id);
          return (
            <div
              key={plan.id}
              className={`rounded-xl border bg-white overflow-hidden ${
                plan.highlighted ? 'border-blue-500 shadow-lg' : 'border-gray-200 shadow-sm'
              }`}
            >
              {plan.highlighted && (
                <div className="bg-blue-600 text-white text-center text-xs font-semibold py-1.5 tracking-wide uppercase">
                  Meest populair
                </div>
              )}
              <div className="p-7">
                <h2 className="text-xl font-bold text-gray-900 mb-1">{plan.name}</h2>
                <div className="mb-6">
                  <span className="text-4xl font-extrabold text-gray-900">{plan.price}</span>
                  <span className="text-gray-500 ml-1">{plan.period}</span>
                </div>

                {current ? (
                  <div className="w-full py-2.5 px-4 rounded-lg text-center text-sm font-medium bg-green-50 text-green-700 border border-green-200 mb-6">
                    Huidig pakket
                  </div>
                ) : (
                  <button
                    onClick={() => handleUpgrade(plan)}
                    disabled={loading === plan.id}
                    className={`w-full py-2.5 px-4 rounded-lg font-semibold text-sm transition-colors mb-6 disabled:opacity-50 disabled:cursor-not-allowed ${
                      plan.highlighted
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    }`}
                  >
                    {loading === plan.id ? 'Verwerken...' : 'Start gratis proefperiode'}
                  </button>
                )}

                <ul className="space-y-3">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <svg className="w-4 h-4 text-green-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>

      {/* FAQ */}
      <div className="bg-white border border-gray-200 rounded-xl p-7">
        <h3 className="text-lg font-semibold text-gray-900 mb-5">Veelgestelde vragen</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            ['Kan ik op elk moment opzeggen?', 'Ja, zeg je abonnement op wanneer je wilt. Geen verplichtingen.'],
            ['Is er een gratis proefperiode?', 'Ja, 14 dagen gratis proberen op alle betaalde pakketten.'],
            ['Kan ik upgraden of downgraden?', 'Ja, wissel van pakket wanneer je wilt via het facturatiemenu.'],
            ['Welke betaalmethoden accepteren jullie?', 'Alle gangbare creditcards via Stripe.'],
          ].map(([q, a]) => (
            <div key={q}>
              <p className="font-medium text-gray-900 text-sm mb-1">{q}</p>
              <p className="text-gray-500 text-sm">{a}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
